import cc from "classcat"
import { Link } from "gatsby"
import { useContext } from "react"
import Slider from "react-slick"
import { LocaleContext } from "contexts"
import useTranslations from "hooks/useTranslations"
import { siteCountry } from "utils/env"
import ArrowMore from "images/arrow-more.svg"

import countries from "../../../config/i18n"

const locales = countries[siteCountry] || countries.fi

const Tag = ({ text, theme = "dark" }) => {
  return (
    <p
      className={cc([
        "m-0 my-2 block w-max rounded-full px-4 py-2 text-sm font-bold",
        {
          "bg-kkm-green text-white": theme === "dark",
          "bg-kkm-darkgreen text-white": theme === "light",
          "bg-kkm-yellow text-zinc-800": theme === "green-yellow",
        },
      ])}
    >
      {text}
    </p>
  )
}

const MediaCard = ({ article, theme }) => {
  const { mediaSectionEnButton } = useTranslations()
  return (
    <div key={article.title} className="flex flex-col pr-6 md:pr-0">
      <Tag text={article.publisherName} theme={theme} />
      <div className="mt-2 flex flex-col space-y-3">
        <div className="grow">
          <h3
            className={cc([
              "m-0 mb-4 block text-xl font-medium ",
              {
                "text-white": theme === "dark",
                "text-zinc-800": theme === "light" || theme === "green-yellow",
              },
            ])}
          >
            {article.content}
          </h3>
        </div>
      </div>
      <a
        href={article.url}
        className={cc([
          "block text-sm font-medium  underline hover:text-teal-600",
          {
            "text-white": theme === "dark",
            "text-kkm-darkgreen": theme === "light",
            "text-zinc-800": theme === "green-yellow",
          },
        ])}
        target="_blank"
        rel="noopener noreferrer"
      >
        {mediaSectionEnButton}
      </a>
    </div>
  )
}

const CustomDot = () => (
  <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16">
    <circle cx="8" cy="8" r="4" fill="currentColor" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16Zm0-2A6 6 0 1 0 8 2a6 6 0 0 0 0 12Z"
      fill="hsl(var(--color-kkm-green)"
    />
  </svg>
)

const sliderSettings = {
  infinite: true,
  slidesToScroll: 1,
  speed: 1000,
  autoplaySpeed: 3000,
  autoplay: true,
  dots: true,
  arrows: false,
  fade: false,
  customPaging: () => (
    <a>
      <CustomDot />
    </a>
  ), // li content with slick-active class on it when active, color applied with tailwind.css
}

export default function MediaEn({ articles, theme = "dark" }) {
  const { mediaSectionEnTitle } = useTranslations()
  const { locale } = useContext(LocaleContext)
  return (
    <div
      className={cc([
        "relative py-16 lg:py-24",
        {
          "bg-zinc-800": theme === "dark",
          "bg-white": theme === "light",
          "bg-kkm-green": theme === "green-yellow",
        },
      ])}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2
          className={cc([
            "mb-8 text-3xl font-extrabold tracking-tight  lg:text-4xl",
            {
              "text-zinc-100": theme === "dark",
              "text-zinc-800": theme === "light",
              "text-white": theme === "green-yellow",
            },
          ])}
        >
          {locales[locale].defaultTitle} {mediaSectionEnTitle}
        </h2>
        <div className="flex space-x-12">
          <div className="w-full">
            <div className="hidden grid-cols-1 gap-y-12 md:grid md:grid-cols-3 md:gap-x-16 md:gap-y-0">
              {articles.map((article) => (
                <MediaCard article={article} theme={theme} />
              ))}
            </div>
            <div className="block md:hidden">
              <Slider
                slidesToShow={1}
                {...sliderSettings}
                dotsClass={
                  theme === "dark"
                    ? "slick-dots green-on-black"
                    : "slick-dots green-on-white"
                }
              >
                {articles.map((article) => (
                  <div className="mb-8 ">
                    <MediaCard
                      id={article.title}
                      article={article}
                      theme={theme}
                    />
                  </div>
                ))}
              </Slider>
            </div>
          </div>
          <Link
            to="/media"
            className={cc([
              "flex w-14  cursor-pointer items-center transition  hover:scale-105",
              {
                "text-white hover:text-kkm-green": theme === "dark",
                "text-zinc-800 hover:text-kkm-green": theme === "light",
                "text-white hover:text-kkm-yellow": theme === "green-yellow",
              },
            ])}
          >
            <ArrowMore />
          </Link>
        </div>
      </div>
    </div>
  )
}
